import { useState } from 'react'

import { cn, numberToCurrency } from '@/utils'
import { monthLabel, monthShortLabel } from '../model/months'
import { missingStatementReason } from '../model/statementReasons'
import type { AccountStatement, MonthKey } from '../model/types'
import { dateRangeText } from '../portfolioText'
import { ReasonActionButton } from '../ReasonActionButton'
import { VerdictBadge } from '../VerdictBadge'
import type { HistoryRow, StatementMoney } from './accountHistory'
import { ShowAllButton } from './ShowAllButton'
import { StatementLink } from './StatementLink'

const ROWS_SHOWN = 12

const HEAD = 'pb-2.5 text-xs font-normal text-ink-subtle'
const CELL = 'py-2.5 align-middle'

const periodText = (statement: AccountStatement) =>
  dateRangeText({
    from: statement.startDate ?? `${statement.month}-01`,
    to: statement.endDate,
  })

const ChangeCell = ({
  row,
}: {
  row: Extract<HistoryRow, { kind: 'statement' }>
}) => {
  if (row.change != null) return numberToCurrency(row.change)
  return (
    <span className="text-ink-subtle">
      {row.missingBefore
        ? `No ${monthShortLabel(row.missingBefore)} statement`
        : 'No earlier statement'}
    </span>
  )
}

const MoneyCell = ({ money }: { money: StatementMoney }) =>
  money.movementCount === 0 ? (
    <span className="text-ink-subtle">None found</span>
  ) : (
    numberToCurrency(money.netMoney)
  )

const StatementRow = ({
  row,
}: {
  row: Extract<HistoryRow, { kind: 'statement' }>
}) => {
  const { statement, reasons } = row
  return (
    <tr>
      <td className={CELL}>{periodText(statement)}</td>
      <td className={cn(CELL, 'text-right')}>
        {statement.value == null ? (
          <span className="text-ink-subtle">No value</span>
        ) : (
          numberToCurrency(statement.value)
        )}
      </td>
      <td className={cn(CELL, 'text-right')}>
        <ChangeCell row={row} />
      </td>
      <td className={cn(CELL, 'text-right')}>
        <MoneyCell money={row.money} />
      </td>
      <td className={cn(CELL, 'text-right')}>
        <VerdictBadge
          status={reasons.length > 0 ? 'needs_review' : 'verified'}
          reason={reasons[0]}
          moreReasons={Math.max(reasons.length - 1, 0)}
        />
      </td>
      <td className={cn(CELL, 'text-right')}>
        <StatementLink
          month={statement.month}
          fileUrl={statement.statementFileUrl}
          fileName={statement.statementFileName}
        />
      </td>
    </tr>
  )
}

const MissingRow = ({ months }: { months: MonthKey[] }) => {
  const first = months[0]
  const last = months[months.length - 1]
  return (
    <tr className="text-ink-subtle">
      <td className={CELL}>
        {months.length === 1
          ? monthLabel(first)
          : `${monthLabel(first)} to ${monthLabel(last)}`}
      </td>
      <td className={cn(CELL, 'text-right')}>
        {months.length === 1 ? 'No statement' : 'No statements'}
      </td>
      <td colSpan={4} className={cn(CELL, 'text-right')}>
        <ReasonActionButton action={missingStatementReason(months).action} />
      </td>
    </tr>
  )
}

const VerifyingRow = ({ month }: { month: MonthKey }) => (
  <tr className="text-ink-subtle">
    <td className={CELL}>{monthLabel(month)}</td>
    <td className={cn(CELL, 'text-right')}>Still being verified</td>
    <td colSpan={4} className={CELL} />
  </tr>
)

const HistoryLine = ({ row }: { row: HistoryRow }) => {
  if (row.kind === 'statement') return <StatementRow row={row} />
  if (row.kind === 'missing') return <MissingRow months={row.months} />
  return <VerifyingRow month={row.statement.month} />
}

const monthCountOf = (rows: HistoryRow[]) =>
  rows.reduce(
    (count, row) => count + (row.kind === 'missing' ? row.months.length : 1),
    0,
  )

const rowKey = (row: HistoryRow) =>
  row.kind === 'statement'
    ? `statement-${row.statement.segmentId}`
    : row.kind === 'missing'
      ? `missing-${row.months[0]}`
      : `verifying-${row.statement.segmentId}`

// One row per statement, newest first, with one "No statement" row for each run of missing months.
export const StatementsTable = ({ rows }: { rows: HistoryRow[] }) => {
  const [showAll, setShowAll] = useState(false)
  const shown = showAll ? rows : rows.slice(0, ROWS_SHOWN)
  return (
    <div className="mt-8">
      <table className="w-full border-collapse text-sm tabular-nums">
        <thead>
          <tr className="border-b text-left">
            <th className={HEAD}>Statement</th>
            <th className={cn(HEAD, 'text-right')}>
              Securities and cash at end
            </th>
            <th className={cn(HEAD, 'text-right')}>Change</th>
            <th className={cn(HEAD, 'text-right')}>Net money in and out</th>
            <th className={HEAD} />
            <th className={HEAD} />
          </tr>
        </thead>
        <tbody className="divide-y">
          {shown.map((row) => (
            <HistoryLine key={rowKey(row)} row={row} />
          ))}
        </tbody>
      </table>
      {!showAll && rows.length > ROWS_SHOWN && (
        <div className="mt-4">
          <ShowAllButton
            count={monthCountOf(rows)}
            noun="months"
            onClick={() => setShowAll(true)}
          />
        </div>
      )}
    </div>
  )
}
